import { Box, Typography, Button } from "@mui/material";
import { useEffect, useState } from "react";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import { downloadTemporaryFileRequest } from "../../../../utils/APICalls";
import ErrorNotification from "../../../../components/error-notification/error-notification";
import Loading from "../../../../components/loading/loading";
import EditInspectorAttachments from "./edit-inspector-attachments";

export default function InspectorAttachments({ attachments, orderId }: any) {
  const [orderAttachments, setOrderAttachments] = useState<any[]>([]);
  const [inspectorAttachments, setInspectorAttachments] = useState<any[]>([]);
  const [editAttachment, setEditAttachment] = useState(false);
  const [showLoader, setShowLoader] = useState(false);
  const [showErrorNotification, setShowErrorNotification] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    if (attachments) {
      const insAttachments = attachments.filter(
        (att: any) => att?.isAddedByInspector
      );
      const ordAttachments = attachments.filter(
        (att: any) => !att?.isAddedByInspector
      );
      setOrderAttachments(ordAttachments);
      setInspectorAttachments(insAttachments);
    }
  }, [attachments]);

  const downloadAttachment = async (data: any) => {
    setShowLoader(true);
    try {
      const response = await downloadTemporaryFileRequest(data.attachmentKey);
      if (response && response.status === 200) {
        let fileUrl = response.data;

        if (typeof fileUrl === "string") {
          fileUrl = fileUrl.trim();
          if (fileUrl.startsWith('"') && fileUrl.endsWith('"')) {
            fileUrl = JSON.parse(fileUrl);
          }
        }

        const a = document.createElement("a");
        a.href = fileUrl;
        a.download = data?.attachmentName;
        a.target = "_blank";
        a.rel = "noopener noreferrer";
        document.body.appendChild(a);
        a.click();
        a.remove();
      } else if (response && response.status >= 400) {
        setShowErrorNotification(true);
        setErrorMessage("Failed to download document. Please try again.");
      }
    } catch (error) {
      setShowErrorNotification(true);
      setErrorMessage("Failed to download document. Please try again.");
    } finally {
      setShowLoader(false);
    }
  };

  return (
    <>
      <Box
        sx={{
          mx: 2,
          my: 3,
          border: "1px solid #C4C4C4",
          borderRadius: 1,
          background: "#FAFAFA",
        }}
      >
        <Typography sx={{ mt: 2, mx: 2 }} variant="h5">
          Order Attachments
        </Typography>

        <Typography sx={{ ml: 2, mr: 12, mt: 1 }}>
          Download order attachments — these may include instructions or forms
          you are required to complete and upload.
        </Typography>

        <Box sx={{ mx: 2, my: 3 }}>
          <Typography sx={{ fontWeight: "550" }}>
            <b>Document</b>
          </Typography>

          {orderAttachments.length === 0 && (
            <Typography mt={1}>No Documents</Typography>
          )}

          {orderAttachments.map((orderAttach: any) => (
            <Box key={orderAttach?.attachmentKey} sx={{ mt: 1 }}>
              <Typography
                key={orderAttach?.attachmentKey}
                component="span"
                color="primary"
                sx={{
                  mr: 2,
                  cursor: "pointer",
                }}
                onClick={() => downloadAttachment(orderAttach)}
              >
                {orderAttach?.attachmentName}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>

      {editAttachment ? (
        <EditInspectorAttachments
          attachments={inspectorAttachments}
          orderId={orderId}
          closeEdit={() => setEditAttachment(false)}
        />
      ) : (
        <Box
          sx={{
            mx: 2,
            my: 3,
            border: "1px solid #C4C4C4",
            borderRadius: 1,
            background: "#FAFAFA",
          }}
        >
          <Box
            sx={{
              mt: 2,
              mx: 2,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography variant="h5">Inspector Attachments</Typography>
            <Button
              variant="text"
              startIcon={<EditOutlinedIcon />}
              onClick={() => setEditAttachment(true)}
            >
              Edit
            </Button>
          </Box>

          <Typography sx={{ ml: 2, mr: 12, mt: 1 }}>
            Upload documents required to be completed and submitted with this
            inspection.
          </Typography>

          <Box sx={{ mx: 2, my: 3 }}>
            <Typography sx={{ fontWeight: "550" }}>
              <b>Document</b>
            </Typography>

            {inspectorAttachments.length === 0 && (
              <Typography mt={1}>No Documents</Typography>
            )}

            {inspectorAttachments.map((orderAttach: any) => (
              <Typography
                key={orderAttach?.attachmentKey}
                component="span"
                color="primary"
                sx={{
                  display: "block",
                  mt: 1,
                  mr: 2,
                  cursor: "pointer",
                }}
                onClick={() => downloadAttachment(orderAttach)}
              >
                {orderAttach?.attachmentName}
              </Typography>
            ))}
          </Box>
        </Box>
      )}

      {showErrorNotification && (
        <ErrorNotification
          show={showErrorNotification}
          message={errorMessage}
          onClose={() => setShowErrorNotification(false)}
        />
      )}

      {showLoader && <Loading />}
    </>
  );
}
